import React, { useState } from 'react';
import { CalendarClock, X } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const formatDateEs = (value) => {
  const raw = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '—';
  const [y, m, d] = raw.split('-');
  return `${d}-${m}-${y}`;
};

/** Modal comercial para aceptar o rechazar una reprogramación de producción. */
const DeliveryRescheduleReviewModal = ({
  isOpen,
  onClose,
  onAccept,
  onReject,
  delivery = null,
  isDarkMode = false,
}) => {
  const ui = useUIColors();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !delivery) return null;

  const run = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'No se pudo completar la acción.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 12000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => !busy && onClose?.()}
    >
      <div
        style={{
          width: 'min(480px, 100%)',
          background: isDarkMode ? '#09090b' : '#fff',
          borderRadius: 12,
          border: `1px solid ${ui.border}`,
          padding: 18,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarClock size={18} color="#9333ea" />
            <h3 style={{ margin: 0, fontSize: 16, color: ui.text }}>Reprogramación de despacho</h3>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', color: ui.muted, cursor: 'pointer' }}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <p style={{ margin: '0 0 12px', fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Producción propuso una nueva fecha para{' '}
          <strong style={{ color: ui.text }}>
            {delivery.delivery_code || delivery.sale_code || 'esta entrega'}
          </strong>
          . Puedes aceptarla o cancelar y reiniciar el pedido.
        </p>

        <div
          style={{
            display: 'grid',
            gap: 8,
            padding: 12,
            borderRadius: 8,
            border: `1px solid ${ui.border}`,
            background: isDarkMode ? '#18181b' : '#f8fafc',
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ color: ui.muted }}>Fecha original</span>
            <strong style={{ color: ui.text }}>
              {formatDateEs(delivery.reschedule_original_date || delivery.scheduled_date)}
            </strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ color: ui.muted }}>Nueva fecha</span>
            <strong style={{ color: ui.text }}>
              {formatDateEs(delivery.reschedule_proposed_date || delivery.scheduled_date)}
            </strong>
          </div>
          <div>
            <div style={{ color: ui.muted, marginBottom: 4 }}>Motivo de reprogramación</div>
            <div style={{ color: ui.text, whiteSpace: 'pre-wrap' }}>
              {String(delivery.reschedule_reason || '').trim() || '—'}
            </div>
          </div>
        </div>

        {error ? (
          <p style={{ margin: '0 0 10px', color: '#dc2626', fontSize: 12 }}>{error}</p>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => onReject?.(delivery))}
            style={{
              height: 34,
              padding: '0 12px',
              borderRadius: 8,
              border: `1px solid ${ui.inputBorder}`,
              background: 'transparent',
              color: '#dc2626',
              fontWeight: 600,
              fontSize: 13,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => onAccept?.(delivery))}
            style={{
              height: 34,
              padding: '0 12px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryRescheduleReviewModal;
